/**
 * The editor's paragraph look (part 10a; tables L, G, J and C in its log): the book look (no gap, first-line indents
 * as the preview sets them) or the block look (half a line between blocks, no indent). In both, the blank lines that
 * separate blocks in the source don't show as lines; the one the cursor stands on shows as the line typing there makes
 * (`pending.ts`, the user's choice after deep-fix r4). The look is decorations only; the text is never touched.
 */
import { Facet, RangeSet } from '@codemirror/state';
import type { Extension, Line, Range, Text } from '@codemirror/state';
import { Decoration, EditorView, ViewPlugin } from '@codemirror/view';
import type { DecorationSet, PluginValue, ViewUpdate } from '@codemirror/view';
import type { SyntaxNode, Tree } from '@lezer/common';
import { chapterField } from './chapter-state.ts';

/** The look: block, or book with the book theme's indent and paragraph spacing (ems of the text) and its break rule. */
export type ParagraphLook =
  | { readonly kind: 'block' }
  | {
      readonly kind: 'book';
      /** A paragraph's first-line indent, in ems (the theme's `firstLineIndent`). */
      readonly indent: number;
      /** The space above a block after a blank line, in ems (the theme's `paragraphSpacing`; none by default). */
      readonly gap: number;
      /** Whether a paragraph after a heading or scene break, or first in the chapter, is indented too (J2, J3). */
      readonly indentAfterBreak: boolean;
    };

/**
 * Whether two looks show the same, field for field. Looks are made by the settings (`paragraphLookOf`) or are
 * {@link BLOCK_LOOK}, in one key order; two equal looks written in another order read as different, which costs one
 * redraw and nothing else.
 */
export function sameLook(a: ParagraphLook, b: ParagraphLook): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The block look: what an editor shows when nobody chose one. */
export const BLOCK_LOOK: ParagraphLook = Object.freeze({ kind: 'block' });

/** The half line the block look leaves between blocks (G). */
export const BLOCK_GAP = '0.5lh';

/** The look the editor shows: the last one given, else the block look. */
export const paragraphLook: Facet<ParagraphLook, ParagraphLook> = Facet.define<ParagraphLook, ParagraphLook>({ combine: (values) => values.at(-1) ?? BLOCK_LOOK });

/**
 * The line classes the look sets: a separator, a line after one (the gap above it, G), a first line indented (J), and a
 * separator holding the cursor, shown as the line typing there makes (table C).
 */
export const LOOK_CLASS = { separator: 'ct-gap', gap: 'ct-gap-after', indent: 'ct-indent', caret: 'ct-gap-caret' } as const;

/** Containers whose line is a block of its own when they hold nothing but their marks (L8). */
const CONTAINERS: ReadonlySet<string> = new Set(['ListItem', 'Blockquote', 'FootnoteDefinition']);
/** Nodes around blocks that hold no line of their own: the chapter and lists. */
const OUTER: ReadonlySet<string> = new Set(['Document', 'BulletList', 'OrderedList']);
/** A container's own markup, which is not content. */
const MARKS: ReadonlySet<string> = new Set(['ListMark', 'QuoteMark', 'FootnoteMark', 'FootnoteLabel']);
/** What part 14's flow passes over when it decides a paragraph's indent: marks, notes and link definitions (J). */
const PASSED: ReadonlySet<string> = new Set([...MARKS, 'FootnoteDefinition', 'LinkReference']);
/** The blocks after which a paragraph is indented only with `indentAfterBreak` (J2). */
const BREAKS: ReadonlySet<string> = new Set(['ATXHeading1', 'ATXHeading2', 'ATXHeading3', 'ATXHeading4', 'ATXHeading5', 'ATXHeading6', 'SetextHeading1', 'SetextHeading2', 'SceneBreak']);

/** Whether `node` holds nothing but its own marks. */
function onlyMarks(node: SyntaxNode): boolean {
  for (let child = node.firstChild; child !== null; child = child.nextSibling) if (!MARKS.has(child.name)) return false;
  return true;
}

/**
 * Table L: whether `line` is a separator: in a chapter holding a block (L11), a line no block has a node on but the
 * containers around it (L3–L7, L12), whose quote marks are all it holds; an empty container starting on it is a block
 * (L8). Every character of a block's line lies in one of its nodes (the grammar has no indented code, decision 35, so even
 * an indented `>` is a paragraph's, L1). Pure; walks the nodes on the line.
 */
export function isSeparator(tree: Tree, line: Line): boolean {
  if (tree.topNode.firstChild === null) return false;
  let separator = true;
  tree.iterate({
    from: line.from,
    to: line.to,
    enter: (node) => {
      if (node.name === 'QuoteMark' || OUTER.has(node.name)) return;
      // A container started on a line before holds this one (blank, or with its blocks on it); one starting here with
      // nothing but its marks is an empty block. Anything else is a block's.
      if (!CONTAINERS.has(node.name) || (node.from >= line.from && onlyMarks(node.node))) separator = false;
    },
  });
  return separator;
}

/** Table J: whether paragraph `node`'s first line is indented in the book look, as part 14's flow indents it. */
export function indented(node: SyntaxNode, indentAfterBreak: boolean): boolean {
  let before = node.prevSibling;
  while (before !== null && PASSED.has(before.name)) before = before.prevSibling;
  // J3: first in the chapter; J4: first in a quote, item or note.
  if (before === null) return node.parent?.name === 'Document' && indentAfterBreak;
  // J2, J1.
  return BREAKS.has(before.name) ? indentAfterBreak : true;
}

/** How a shown line looks: a gap above it (the line before is a separator, G) and its first-line indent (J). */
export interface LineLook {
  readonly gap: boolean;
  readonly indent: boolean;
}

/** The paragraph whose first line is `line`, or null. */
function paragraphStarting(tree: Tree, line: Line): SyntaxNode | null {
  let found: SyntaxNode | null = null;
  tree.iterate({
    from: line.from,
    to: line.to,
    enter: (node) => {
      // One block starts on a line at most; a paragraph that started on a line before is not this one's.
      if (node.name === 'Paragraph' && node.from >= line.from) found = node.node;
    },
  });
  return found;
}

/** Tables G and J for one shown line. Pure. */
export function lineLook(tree: Tree, doc: Text, line: Line, look: ParagraphLook): LineLook {
  const gap = line.number > 1 && isSeparator(tree, doc.line(line.number - 1));
  if (look.kind === 'block') return { gap, indent: false };
  const paragraph = paragraphStarting(tree, line);
  return { gap, indent: paragraph !== null && indented(paragraph, look.indentAfterBreak) };
}

/** One line's classes, as data: a line decoration at `at`. */
export interface LookLine {
  readonly at: number;
  readonly className: string;
}

/** The line classes of every line `from`–`to` meets (tables L, G, J). Pure; costs those lines. */
export function lookLines(tree: Tree, doc: Text, from: number, to: number, look: ParagraphLook): LookLine[] {
  const out: LookLine[] = [];
  const last = doc.lineAt(to).number;
  for (let n = doc.lineAt(from).number; n <= last; n += 1) {
    const line = doc.line(n);
    if (isSeparator(tree, line)) {
      out.push({ at: line.from, className: LOOK_CLASS.separator });
      continue;
    }
    const { gap, indent } = lineLook(tree, doc, line, look);
    if (gap) out.push({ at: line.from, className: LOOK_CLASS.gap });
    if (indent) out.push({ at: line.from, className: LOOK_CLASS.indent });
  }
  return out;
}

/** The look's measures as CSS lengths: the indent and the gap (G). */
export function lookLengths(look: ParagraphLook): { readonly indent: string; readonly gap: string } {
  return look.kind === 'block' ? { indent: '0', gap: BLOCK_GAP } : { indent: `${look.indent}em`, gap: `${look.gap}em` };
}

/** The line decorations for what is on screen. */
function decorationsOf(view: EditorView): DecorationSet {
  const { state } = view;
  const { tree } = state.field(chapterField);
  const look = state.facet(paragraphLook);
  const ranges: Range<Decoration>[] = [];
  for (const { from, to } of view.visibleRanges) {
    for (const line of lookLines(tree, state.doc, from, to, look)) ranges.push(Decoration.line({ class: line.className }).range(line.at));
  }
  // Lines in order, each line's classes at its start: sorted as they come.
  return RangeSet.of(ranges);
}

class LookPlugin implements PluginValue {
  decorations: DecorationSet;
  private readonly view: EditorView;

  constructor(view: EditorView) {
    this.view = view;
    this.decorations = decorationsOf(view);
    this.measures();
  }

  /** Every update: what is on screen, its lines and the look's lengths are read again (a screen of lines is cheap). */
  update(update: ViewUpdate): void {
    this.measures();
    this.decorations = decorationsOf(update.view);
  }

  /** The look's lengths as the editor's CSS variables, through the CSSOM (the app's CSP refuses style attributes). */
  private measures(): void {
    const { indent, gap } = lookLengths(this.view.state.facet(paragraphLook));
    this.view.dom.style.setProperty('--ct-indent', indent);
    this.view.dom.style.setProperty('--ct-gap', gap);
  }
}

/** The plugin that sets the look's line classes for what is on screen. */
export const lookPlugin: ViewPlugin<LookPlugin> = ViewPlugin.fromClass(LookPlugin, { decorations: (plugin) => plugin.decorations });

/** The paragraph look in an editor: `look`, its line classes and the editor's class naming it. */
export function lookView(look: ParagraphLook = BLOCK_LOOK): Extension {
  return [
    paragraphLook.of(look),
    lookPlugin,
    EditorView.editorAttributes.compute([paragraphLook], (state) => ({ class: `ct-look-${state.facet(paragraphLook).kind}` })),
  ];
}
